/**
 * A VARIAVEL @NOME — texto de campanha de fly que se abre com o primeiro nome do contato
 * (pedido dele, 21/09/2026, junto do botao "Somente Texto").
 *
 * ⛔ Pura e compartilhada entre front e back pelo mesmo motivo do `fly-quantidade`: a previa da
 * tela e o envio do worker tem que produzir o MESMO resultado (DA-591/DA-682) — duas
 * implementacoes do mesmo julgamento viram disfarce, nao conferencia.
 *
 * ⚠️ As DUAS resolvem no ENVIO, por destinatario. `@NOME` sempre foi assim; `@FREQUENCIA`
 * passou a ser em 15/10/2026 ([DA-811]), quando a regua virou POR ETIQUETA — o eletricista
 * recebe todo dia e o consumidor 1 vez por semana, entao a frase nao e' mais igual pra todo
 * mundo. O texto gravado na campanha guarda as duas LITERAIS; quem resolve e' o worker, um por
 * um, no instante de mandar. A previa mostra um EXEMPLO, com um contato real da lista.
 */

/**
 * A frequencia em palavras, DERIVADA do numero de dias — nunca escrita a mao.
 *
 * ⛔ E' o ponto inteiro da variavel existir: texto fixo dizendo "1 vez por semana" viraria
 * mentira no dia em que a regua mudasse, numa promessa feita ao CLIENTE, por escrito.
 */
export function frequenciaEmPalavras(dias: number): string {
  if (dias <= 1) return '1 vez por dia';
  if (dias === 7) return '1 vez por semana';
  if (dias % 7 === 0) return '1 vez a cada ' + dias / 7 + ' semanas';
  return '1 vez a cada ' + dias + ' dias';
}

/**
 * Troca `@FREQUENCIA` pela frequencia DESTA pessoa ([DA-811]). Substituicao literal.
 *
 * ⚠️ Roda ANTES do `resolverNomeContato` por seguranca de prefixo: no dia em que existir uma
 * variavel que comece com `@FREQUENCIA`, a mais longa tem que ser trocada primeiro.
 */
export function resolverFrequencia(texto: string, dias: number): string {
  if (!texto.includes('@FREQUENCIA')) return texto;
  return texto.split('@FREQUENCIA').join(frequenciaEmPalavras(dias));
}

/**
 * O primeiro nome do contato, capitalizado. Contato sem nome utilizavel — vazio, ou so'
 * digitos/pontuacao (numero salvo como nome, por exemplo) — devolve string vazia: quem monta a
 * mensagem final e' quem decide o que fazer com a ausencia (ver `resolverNomeContato`).
 */
export function primeiroNomeContato(nomeContato: string | null | undefined): string {
  if (!nomeContato) return '';
  const primeira = nomeContato.trim().split(/\s+/)[0] ?? '';
  // "so' digitos/pontuacao" nao e' nome utilizavel — sem isto um contato salvo como "556492..."
  // devolveria o proprio numero como "nome".
  if (!/[a-zA-ZÀ-ÖØ-öø-ÿ]/.test(primeira)) return '';
  return primeira.charAt(0).toLocaleUpperCase('pt-BR') + primeira.slice(1).toLocaleLowerCase('pt-BR');
}

/**
 * Troca `@NOME` pelo primeiro nome do contato — e, quando nao ha nome, tira a pontuacao solta
 * que a variavel deixaria pra tras. Decisao dele: "Ola @NOME, tudo bem!" sem nome vira
 * "Ola, tudo bem!", NUNCA "Ola , tudo bem!".
 *
 * ⚠️ Roda linha a linha (nao no texto inteiro) pra nao atravessar quebras de linha ao limpar
 * espaco duplo ou pontuacao — a mensagem pode ter varias linhas e cada uma se limpa sozinha.
 */
export function resolverNomeContato(texto: string, nomeContato: string | null | undefined): string {
  if (!texto.includes('@NOME')) return texto;
  const nome = primeiroNomeContato(nomeContato);
  if (nome) return texto.split('@NOME').join(nome);
  return texto
    .split('\n')
    .map((linha) => linha
      .split('@NOME').join('')
      // "Ola @NOME," sem nome vira "Ola ," — o espaco antes da pontuacao some.
      .replace(/[ \t]+([,.!?;:])/g, '$1')
      // espacos duplos que a remocao deixou no meio da frase.
      .replace(/[ \t]{2,}/g, ' ')
      .trim())
    .join('\n');
}
